import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { BrioSelect } from '../../components/BrioComponents';
import { getClipboardRows, setClipboardRows, subscribeClipboard } from '../utils/measurementClipboard';
import { useRangeSelection } from '../hooks/useRangeSelection';
import { useSort } from '../hooks/useSort';
import { SortHeader } from './SortHeader';
import { formatQty } from '../utils/money';
import { StickyScrollBox } from './StickyScrollbar';

const BASIS_COL = {
  running_m: 'runningM',
  plan_area: 'planArea',
  elevation_area: 'elevationArea',
  volume: 'volume',
};

const EDITABLE_COLS = ['description', 'nr', 'length', 'breadth', 'height', 'projectZoneId', 'level', 'gridRef', 'locationNote'];
const NUMERIC_COLS = new Set(['nr', 'length', 'breadth', 'height']);

const fmt = formatQty;

const cellInput = {
  width: '100%', border: '1px solid transparent', background: 'transparent',
  padding: '0.25rem 0.3rem', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box',
};
const numInput = { ...cellInput, textAlign: 'right' };
const directInput = { ...numInput, border: '1px solid var(--brio-border, #ccc)', background: '#fff' };
const opBtn = {
  fontSize: '0.76rem', padding: '0.25rem 0.6rem', background: 'transparent',
  border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, cursor: 'pointer',
};

const measurementToRowInput = (m, extraLinks = []) => {
  const links = [...(m.links || [])];
  extraLinks.forEach((el) => { if (!links.some((l) => l.itemId === el.itemId)) links.push(el); });
  return {
    description: m.description ?? null,
    mode: m.mode || 'prism',
    nr: m.nr ?? 1,
    length: m.length ?? null,
    breadth: m.breadth ?? null,
    height: m.height ?? null,
    runningM: m.runningM ?? null,
    planArea: m.planArea ?? null,
    elevationArea: m.elevationArea ?? null,
    volume: m.volume ?? null,
    projectZoneId: m.projectZoneId ?? null,
    level: m.level ?? null,
    gridRef: m.gridRef ?? null,
    locationNote: m.locationNote ?? null,
    links,
  };
};

const draftFrom = (m) => ({
  description: m.description ?? '',
  mode: m.mode || 'prism',
  nr: m.nr ?? 1,
  length: m.length ?? '',
  breadth: m.breadth ?? '',
  height: m.height ?? '',
  runningM: m.runningM ?? '',
  planArea: m.planArea ?? '',
  elevationArea: m.elevationArea ?? '',
  volume: m.volume ?? '',
  projectZoneId: m.projectZoneId ?? '',
  level: m.level ?? '',
  gridRef: m.gridRef ?? '',
  locationNote: m.locationNote ?? '',
});

function Row({ m, zoneOptions, basis, showFeeds, codeForItem, lineId, isDeduct, selected, onToggle, onCommit, onDelete, onCellFocus, onToggleDeduct }) {
  const [draft, setDraft] = useState(() => draftFrom(m));
  useEffect(() => {
    setDraft(draftFrom(m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.mode, m.description, m.nr, m.length, m.breadth, m.height, m.runningM, m.planArea, m.elevationArea, m.volume, m.projectZoneId, m.level, m.gridRef, m.locationNote]);

  const direct = draft.mode === 'direct';
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const commit = () => onCommit(m.id, draft);
  const focus = (colKey) => () => onCellFocus(m.id, colKey);
  const activeKey = BASIS_COL[basis];
  const hi = (key) => (key === activeKey ? { background: 'var(--brio-gold-faint, #f7f1d4)', fontWeight: 600 } : null);
  const redIf = isDeduct ? { color: '#a94442' } : null;

  const dimInput = (k) => (
    <input style={{ ...numInput, ...(direct ? { color: '#bbb' } : null) }} type="number" step="any"
      value={draft[k]} onChange={set(k)} onBlur={commit} onFocus={focus(k)} disabled={direct} />
  );
  // A measure cell: editable input in direct mode, computed read-only otherwise.
  const measureCell = (draftKey, mVal, hiKey) => (
    <td style={{ padding: direct ? 0 : '0.25rem 0.4rem', textAlign: 'right', color: 'var(--brio-text-muted)', ...hi(hiKey), ...redIf }}>
      {direct
        ? <input style={directInput} type="number" step="any" value={draft[draftKey]} onChange={set(draftKey)} onBlur={commit} />
        : fmt(mVal)}
    </td>
  );

  const handleModeToggle = () => {
    setDraft((d) => {
      const next = { ...d, mode: d.mode === 'direct' ? 'prism' : 'direct' };
      // commit on next tick with the new mode
      setTimeout(() => onCommit(m.id, next), 0);
      return next;
    });
  };

  return (
    <tr className="qs-row" style={{ borderBottom: '1px solid var(--brio-border-subtle, #eee)', background: selected ? '#f0e9c2' : (isDeduct ? '#fdf4f4' : undefined) }}>
      <td style={{ padding: '0 0.3rem', textAlign: 'center' }}>
        <input type="checkbox" checked={selected} onChange={() => {}} onClick={(e) => onToggle(m.id, e)} />
      </td>
      <td style={{ padding: '0 0.2rem', textAlign: 'center' }}>
        <button type="button" onClick={handleModeToggle} title={direct ? 'Switch to L×B×H' : 'Switch to direct area/volume entry'}
          style={{ fontSize: '0.66rem', padding: '0.1rem 0.3rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, background: direct ? 'var(--brio-gold-faint, #f7f1d4)' : 'transparent', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {direct ? 'Direct' : 'L×B×H'}
        </button>
      </td>
      {lineId && (
        <td style={{ padding: '0 0.3rem', textAlign: 'center' }} title="Deduct from this line">
          <input type="checkbox" checked={!!isDeduct} onChange={(e) => onToggleDeduct(m.id, e.target.checked)} />
        </td>
      )}
      {showFeeds && (
        <td style={{ padding: '0.25rem 0.4rem', fontFamily: 'monospace', fontSize: '0.74rem', color: 'var(--brio-text-muted)' }}>
          {(m.links || []).map((l) => `${codeForItem(l.itemId)}${l.isDeduction ? ' (−)' : ''}`).join(', ') || '—'}
        </td>
      )}
      <td style={{ padding: 0 }}>
        <input style={{ ...cellInput, ...redIf }} value={draft.description} onChange={set('description')} onBlur={commit} onFocus={focus('description')} placeholder={isDeduct ? 'Ddt (description)' : '(description)'} />
      </td>
      <td style={{ padding: 0 }}>
        <input style={numInput} type="number" step="any" value={draft.nr} onChange={set('nr')} onBlur={commit} onFocus={focus('nr')} />
      </td>
      <td style={{ padding: 0 }}>{dimInput('length')}</td>
      <td style={{ padding: 0 }}>{dimInput('breadth')}</td>
      <td style={{ padding: 0 }}>{dimInput('height')}</td>
      {measureCell('runningM', m.runningM, 'runningM')}
      {measureCell('planArea', m.planArea, 'planArea')}
      {measureCell('elevationArea', m.elevationArea, 'elevationArea')}
      {measureCell('volume', m.volume, 'volume')}
      <td style={{ padding: 0 }}>
        <BrioSelect value={draft.projectZoneId} onChange={set('projectZoneId')} onBlur={commit} onFocus={focus('projectZoneId')} style={{ fontSize: '0.8rem', padding: '0.2rem' }}>
          <option value="">—</option>
          {zoneOptions.map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
        </BrioSelect>
      </td>
      <td style={{ padding: 0 }}><input style={cellInput} value={draft.level} onChange={set('level')} onBlur={commit} onFocus={focus('level')} placeholder="level" /></td>
      <td style={{ padding: 0 }}><input style={cellInput} value={draft.gridRef} onChange={set('gridRef')} onBlur={commit} onFocus={focus('gridRef')} placeholder="grid" /></td>
      <td style={{ padding: 0 }}><input style={cellInput} value={draft.locationNote} onChange={set('locationNote')} onBlur={commit} onFocus={focus('locationNote')} placeholder="note" /></td>
      <td style={{ padding: '0 0.3rem', textAlign: 'right' }}>
        <button type="button" onClick={() => onDelete(m.id)} title="Remove row"
          style={{ border: 'none', background: 'transparent', color: '#a94442', cursor: 'pointer', fontSize: '0.9rem' }}>×</button>
      </td>
    </tr>
  );
}

// A full-width drawer header for a trade chapter or sub-trade band, rendered
// inline in the sheet's <tbody> between the measurement rows it heads. Clicking
// it toggles the section open/shut. Mirrors the Bill grid's section banners so
// the take-off reads chapter → sub-trade like the priced bill.
function GroupHeaderRow({ entry, cols, onToggle }) {
  const isChapter = entry.kind === 'chapter';
  const Chevron = entry.collapsed ? ChevronRight : ChevronDown;
  const toggle = () => { if (onToggle) onToggle(entry.key); };
  return (
    <tr>
      <td
        colSpan={cols}
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        title={entry.collapsed ? 'Expand section' : 'Collapse section'}
        style={{
          cursor: 'pointer', userSelect: 'none',
          padding: isChapter ? '0.45rem 0.5rem' : '0.32rem 0.5rem 0.32rem 1.75rem',
          background: isChapter ? 'var(--brio-gold-faint, #f7f1d4)' : 'var(--brio-surface-alt, #f6f4ee)',
          borderTop: isChapter ? '1px solid var(--brio-border, #ddd)' : '1px solid var(--brio-border-subtle, #eee)',
          fontFamily: 'Raleway, sans-serif',
          fontWeight: isChapter ? 700 : 600,
          fontSize: isChapter ? '0.9rem' : '0.82rem',
          color: entry.isUnlinked ? 'var(--brio-text-muted)' : 'var(--brio-text, #333)',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Chevron size={isChapter ? 15 : 13} style={{ flexShrink: 0, opacity: 0.75 }} />
          <span>
            {entry.label}
            <span style={{ fontWeight: 400, fontSize: '0.75rem', color: 'var(--brio-text-muted)', marginLeft: 8 }}>
              {entry.count} {entry.count === 1 ? 'row' : 'rows'}
            </span>
          </span>
        </span>
      </td>
    </tr>
  );
}

function MeasurementTable({
  rows,
  zoneOptions,
  basis = '',
  showFeeds = false,
  codeForItem = () => '?',
  lineId = null,
  defaultItemIds = [],
  onCommit,
  onDelete,
  onCreateRows,
  onDeleteMany,
  onApplyPaste,
  onToggleDeduct,
  emptyHint = 'No measurements yet.',
  // When provided the sheet renders grouped: an ordered list of
  // { kind:'chapter'|'subtrade'|'row', ... } entries (built by the parent from
  // groupMeasurementsBySubtrade, honouring collapse). Sorting is disabled in
  // this mode — the grouping owns row order. Absent → the flat, sortable list.
  renderList = null,
  onToggleGroup,
}) {
  // Click-to-sort (same pattern as the other tables); preserves take-off order
  // until a header is clicked. Nulls/blank dimensions sort last.
  const { sorted: sortedRows, sort, toggle: toggleSort } = useSort(rows, {
    description: (r) => r.description || '',
    nr: (r) => r.nr,
    length: (r) => r.length,
    breadth: (r) => r.breadth,
    height: (r) => r.height,
    running_m: (r) => r.runningM,
    plan_area: (r) => r.planArea,
    elevation_area: (r) => r.elevationArea,
    volume: (r) => r.volume,
    level: (r) => r.level || '',
  });
  const grouped = Array.isArray(renderList);
  // Rows actually visible in the body: the flat sorted list, or — when grouped —
  // the measurement rows pulled out of the render list in section order (rows in
  // collapsed sections are already omitted upstream). Selection, copy/paste and
  // the shift-range anchor all key off this so they line up with what's on screen.
  const dataRows = grouped ? renderList.filter((e) => e.kind === 'row').map((e) => e.m) : sortedRows;
  const { selected, toggle, toggleAll, clear } = useRangeSelection(dataRows.map((r) => r.id));
  const clipboard = useSyncExternalStore(subscribeClipboard, getClipboardRows);
  const [busy, setBusy] = useState(false);
  const activeCell = useRef(null);

  const zoneByText = useMemo(() => {
    const m = new Map();
    zoneOptions.forEach((z) => { m.set(z.label, z.id); if (z.code) m.set(z.code, z.id); });
    return m;
  }, [zoneOptions]);

  const allSelected = dataRows.length > 0 && dataRows.every((r) => selected.has(r.id));
  const selectedRows = useMemo(() => dataRows.filter((r) => selected.has(r.id)), [dataRows, selected]);

  const extraLinks = useMemo(() => defaultItemIds.map((itemId) => ({ itemId, isDeduction: false })), [defaultItemIds]);
  const cols = 16 + (lineId ? 1 : 0) + (showFeeds ? 1 : 0);

  const handleCopy = () => { if (selectedRows.length) setClipboardRows(selectedRows.map((m) => measurementToRowInput(m))); };
  const handleCut = async () => {
    if (!selectedRows.length) return;
    setClipboardRows(selectedRows.map((m) => measurementToRowInput(m)));
    setBusy(true); await onDeleteMany([...selected]); setBusy(false); clear();
  };
  const handleDuplicate = async () => {
    if (!selectedRows.length) return;
    setBusy(true); await onCreateRows(selectedRows.map((m) => measurementToRowInput(m, extraLinks))); setBusy(false); clear();
  };
  const handlePaste = async () => {
    if (!clipboard || clipboard.length === 0) return;
    setBusy(true);
    await onCreateRows(clipboard.map((c) => {
      const links = [...(c.links || [])];
      extraLinks.forEach((el) => { if (!links.some((l) => l.itemId === el.itemId)) links.push(el); });
      return { ...c, links };
    }));
    setBusy(false);
  };
  const handleDeleteSelected = async () => {
    if (!selectedRows.length) return;
    setBusy(true); await onDeleteMany([...selected]); setBusy(false); clear();
  };

  const parseVal = useCallback((colKey, raw) => {
    const v = (raw ?? '').trim();
    if (colKey === 'projectZoneId') {
      if (!v) return null;
      return zoneByText.has(v) ? zoneByText.get(v) : undefined;
    }
    if (NUMERIC_COLS.has(colKey)) {
      if (v === '') return null;
      const cleaned = v.replace(/[,$€£₹\s]/g, '');
      const num = Number(cleaned);
      return isNaN(num) ? undefined : num;
    }
    return v === '' ? null : v;
  }, [zoneByText]);

  const handleCellPaste = useCallback(async (e) => {
    const active = activeCell.current;
    if (!active || !onApplyPaste) return;
    const text = e.clipboardData?.getData('text');
    if (!text) return;
    const isGrid = text.includes('\t') || /\r?\n/.test(text.replace(/\s+$/, ''));
    if (!isGrid) return;
    e.preventDefault();
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    const matrix = text.replace(/\r/g, '').replace(/\n+$/, '').split('\n').map((l) => l.split('\t'));
    const startCol = EDITABLE_COLS.indexOf(active.colKey);
    const startRow = dataRows.findIndex((r) => r.id === active.rowId);
    if (startCol < 0 || startRow < 0) return;
    const updates = [];
    const newRows = [];
    matrix.forEach((cells, dr) => {
      const overrides = {};
      cells.forEach((cell, dc) => {
        const colKey = EDITABLE_COLS[startCol + dc];
        if (!colKey) return;
        const parsed = parseVal(colKey, cell);
        if (parsed === undefined) return;
        overrides[colKey] = parsed;
      });
      if (Object.keys(overrides).length === 0) return;
      const targetIdx = startRow + dr;
      if (targetIdx < dataRows.length) updates.push({ id: dataRows[targetIdx].id, fields: overrides });
      else newRows.push(overrides);
    });
    if (updates.length === 0 && newRows.length === 0) return;
    setBusy(true); await onApplyPaste({ updates, newRows }); setBusy(false);
  }, [dataRows, parseVal, onApplyPaste]);

  const renderMeasurementRow = (m) => (
    <Row
      key={m.id}
      m={m}
      zoneOptions={zoneOptions}
      basis={basis}
      showFeeds={showFeeds}
      codeForItem={codeForItem}
      lineId={lineId}
      isDeduct={lineId ? !!(m.links || []).find((l) => l.itemId === lineId)?.isDeduction : false}
      selected={selected.has(m.id)}
      onToggle={toggle}
      onCommit={onCommit}
      onDelete={onDelete}
      onCellFocus={(rowId, colKey) => { activeCell.current = { rowId, colKey }; }}
      onToggleDeduct={onToggleDeduct}
    />
  );

  return (
    <div onPasteCapture={handleCellPaste}>
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>
          {selected.size > 0 ? `${selected.size} selected` : 'Row tools:'}
        </span>
        <button type="button" style={opBtn} onClick={handleCopy} disabled={selected.size === 0 || busy}>Copy</button>
        <button type="button" style={opBtn} onClick={handleCut} disabled={selected.size === 0 || busy}>Cut</button>
        <button type="button" style={opBtn} onClick={handleDuplicate} disabled={selected.size === 0 || busy}>Duplicate</button>
        <button type="button" style={opBtn} onClick={handlePaste} disabled={!clipboard || clipboard.length === 0 || busy}>
          Paste rows{clipboard && clipboard.length ? ` (${clipboard.length})` : ''}
        </button>
        <button type="button" style={{ ...opBtn, color: '#a94442', borderColor: '#a94442' }} onClick={handleDeleteSelected} disabled={selected.size === 0 || busy}>
          Delete selected
        </button>
        <span style={{ fontSize: '0.72rem', color: 'var(--brio-text-muted)', marginLeft: '0.25rem' }}>
          Tip: click a cell and Ctrl/Cmd+V to paste a block from Excel.
        </span>
      </div>
      <StickyScrollBox className="qs-table-scroll">
        <table className="qs-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1040 + (lineId ? 40 : 0) + (showFeeds ? 110 : 0) }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--brio-border, #ddd)', color: 'var(--brio-text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px', fontSize: 10, fontWeight: 600 }}>
              <th style={{ padding: '0.3rem', width: 28, textAlign: 'center' }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              </th>
              <th style={{ padding: '0.3rem', width: 56 }}>Type</th>
              {lineId && <th style={{ padding: '0.3rem', width: 36, textAlign: 'center' }}>Ddt</th>}
              {showFeeds && <th style={{ padding: '0.3rem', width: 110 }}>Feeds</th>}
              <SortHeader field="description" sort={sort} onSort={toggleSort} disabled={grouped} style={{ padding: '0.3rem' }}>Description</SortHeader>
              <SortHeader field="nr" sort={sort} onSort={toggleSort} disabled={grouped} align="right" style={{ padding: '0.3rem', width: 60 }}>Nr</SortHeader>
              <SortHeader field="length" sort={sort} onSort={toggleSort} disabled={grouped} align="right" style={{ padding: '0.3rem', width: 70 }}>L</SortHeader>
              <SortHeader field="breadth" sort={sort} onSort={toggleSort} disabled={grouped} align="right" style={{ padding: '0.3rem', width: 70 }}>B</SortHeader>
              <SortHeader field="height" sort={sort} onSort={toggleSort} disabled={grouped} align="right" style={{ padding: '0.3rem', width: 70 }}>H</SortHeader>
              <SortHeader field="running_m" sort={sort} onSort={toggleSort} disabled={grouped} align="right" style={{ padding: '0.3rem', width: 78 }}>Run m</SortHeader>
              <SortHeader field="plan_area" sort={sort} onSort={toggleSort} disabled={grouped} align="right" style={{ padding: '0.3rem', width: 78 }}>Plan m²</SortHeader>
              <SortHeader field="elevation_area" sort={sort} onSort={toggleSort} disabled={grouped} align="right" style={{ padding: '0.3rem', width: 78 }}>Elev m²</SortHeader>
              <SortHeader field="volume" sort={sort} onSort={toggleSort} disabled={grouped} align="right" style={{ padding: '0.3rem', width: 78 }}>Vol m³</SortHeader>
              <th style={{ padding: '0.3rem', width: 130 }}>Zone</th>
              <SortHeader field="level" sort={sort} onSort={toggleSort} disabled={grouped} style={{ padding: '0.3rem', width: 70 }}>Level</SortHeader>
              <th style={{ padding: '0.3rem', width: 60 }}>Grid</th>
              <th style={{ padding: '0.3rem' }}>Note</th>
              <th style={{ padding: '0.3rem', width: 28 }} />
            </tr>
          </thead>
          <tbody>
            {dataRows.length === 0 ? (
              <tr><td colSpan={cols} style={{ padding: '0.75rem', color: 'var(--brio-text-muted)' }}>{emptyHint}</td></tr>
            ) : grouped ? (
              renderList.map((entry) => (
                entry.kind === 'row'
                  ? renderMeasurementRow(entry.m)
                  : <GroupHeaderRow key={entry.key} entry={entry} cols={cols} onToggle={onToggleGroup} />
              ))
            ) : (
              sortedRows.map(renderMeasurementRow)
            )}
          </tbody>
        </table>
      </StickyScrollBox>
    </div>
  );
}

export default MeasurementTable;
